
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useQueryClient } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"
import { BasicInfoSection } from "./details/BasicInfoSection"
import { MedicalHistorySection } from "./details/MedicalHistorySection"
import { MedicationsSection } from "./details/MedicationsSection"
import { RenewalHistorySection } from "./details/RenewalHistorySection"
import { ShippingSection } from "./details/ShippingSection"
import { useAssessmentDetails } from "./hooks/useAssessmentDetails"

interface PatientDetailsModalProps {
  patientId: string | null
  assessmentId: string | null  
  isOpen: boolean
  onClose: () => void
}

export const PatientDetailsModal = ({ patientId, assessmentId, isOpen, onClose }: PatientDetailsModalProps) => {
  const { data: assessments, isLoading } = useAssessmentDetails(patientId)
  const queryClient = useQueryClient()
  
  // Get the most recent assessment for patient details
  const currentAssessment = assessments?.[0]

  const handleDeletionSuccess = () => {
    // Invalidate and refetch assessments after deletion
    queryClient.invalidateQueries({ queryKey: ["patient-assessments", patientId] })
    queryClient.invalidateQueries({ queryKey: ["provider-assessments"] })
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle>Patient Details</DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex-grow flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading patient information...</p>
            </div>
          </div>
        ) : !currentAssessment ? (
          <div className="flex-grow flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">No patient data found</p>
            </div>
          </div>
        ) : (
          <ScrollArea className="flex-grow px-6">
            <div className="space-y-8 py-6">
              <ShippingSection assessment={currentAssessment} />
              <RenewalHistorySection 
                assessment={currentAssessment} 
                allAssessments={assessments || []} 
                onDeleteSuccess={handleDeletionSuccess}
              />
              <BasicInfoSection assessment={currentAssessment} />
              <div className="space-y-8">
                <MedicalHistorySection assessment={currentAssessment} />
                <MedicationsSection assessment={currentAssessment} />
              </div>
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  )
}
